import api from '@/shared/api';
import { buildTruthySearchParams, requestExcelBlobExport } from '@/shared/lib';
import type { SortOrder } from '@/shared/lib';
import type {
  CreateTrainingMatrixRevisionEntry,
  CreateTrainingMatrixRevisionResponse,
  DeleteTrainingMatrixPositionsEntriesResponse,
  SaveTrainingMatrixRevisionResponse,
  TrainingMatrixCurrentRevisionResponse,
  TrainingMatrixRevisionDetailResponse,
  TrainingMatrixRevisionExistsResponse,
  TrainingMatrixRevisionListResponse,
  TrainingMatrixSectionDocumentsResponse,
  UpdateTrainingMatrixEntriesResponse,
} from '../model/trainingMatrix.types';

export type TrainingMatrixSearchField = 'cargo' | 'prefixo' | 'titulo';
export type TrainingMatrixSort = 'createdAt' | 'name';
export type TrainingMatrixOrder = SortOrder;
export type TrainingMatrixDocumentPrefix = 'MQ' | 'PR' | 'IT' | 'DA';
export type TrainingMatrixSectionSearchField = 'prefixo' | 'titulo';

export interface GetTrainingMatrixSectionDocumentsParams {
  prefixCode: TrainingMatrixDocumentPrefix;
  searchField?: TrainingMatrixSectionSearchField;
  search?: string;
}

export interface GetCurrentTrainingMatrixParams {
  page?: number;
  itemsPerPage?: number;
  allItems?: boolean;
  search?: string;
  searchField?: TrainingMatrixSearchField;
  sort?: TrainingMatrixSort;
  order?: TrainingMatrixOrder;
  positionId?: number;
  documentPrefix?: TrainingMatrixDocumentPrefix;
}

export interface ExportTrainingMatrixExcelParams {
  search?: string;
  searchField?: TrainingMatrixSearchField;
  sort?: TrainingMatrixSort;
  order?: TrainingMatrixOrder;
  positionId?: number;
  documentPrefix?: TrainingMatrixDocumentPrefix;
}

const TRAINING_MATRIX_EXPORT_QUERY_KEYS = [
  'search',
  'searchField',
  'sort',
  'order',
  'positionId',
  'documentPrefix',
] as const;

export interface ListTrainingMatrixRevisionsParams {
  page?: number;
  itemsPerPage?: number;
}

export interface GetTrainingMatrixRevisionDetailParams {
  page?: number;
  itemsPerPage?: number;
  allItems?: boolean;
  positionId?: number;
}

class TrainingMatrixApi {
  async getCurrent(
    params: GetCurrentTrainingMatrixParams = {}
  ): Promise<TrainingMatrixCurrentRevisionResponse> {
    const response = await api.get<TrainingMatrixCurrentRevisionResponse>(
      '/trainings/matrix/current',
      { params }
    );
    return response.data;
  }

  async exportExcel(
    params: ExportTrainingMatrixExcelParams
  ): Promise<{ blob: Blob; exportedCount: number; filename: string }> {
    const queryParams = buildTruthySearchParams(params, TRAINING_MATRIX_EXPORT_QUERY_KEYS);
    return requestExcelBlobExport(
      () =>
        api.get(`/trainings/matrix/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      'matriz-treinamentos.xlsx',
      'Tente novamente.'
    );
  }

  async createRevision(
    entries: CreateTrainingMatrixRevisionEntry[]
  ): Promise<CreateTrainingMatrixRevisionResponse> {
    const response = await api.post<CreateTrainingMatrixRevisionResponse>('/trainings/matrix', {
      entries,
    });
    return response.data;
  }

  async checkRevisionExists(revision: number): Promise<TrainingMatrixRevisionExistsResponse> {
    const response = await api.get<TrainingMatrixRevisionExistsResponse>(
      `/trainings/matrix/revisions/${revision}/exists`
    );
    return response.data;
  }

  async saveRevision(
    revision: number,
    entries: CreateTrainingMatrixRevisionEntry[]
  ): Promise<SaveTrainingMatrixRevisionResponse> {
    const response = await api.put<SaveTrainingMatrixRevisionResponse>(
      `/trainings/matrix/revisions/${revision}`,
      { entries }
    );
    return response.data;
  }

  async listRevisions(
    params: ListTrainingMatrixRevisionsParams = {}
  ): Promise<TrainingMatrixRevisionListResponse> {
    const response = await api.get<TrainingMatrixRevisionListResponse>('/trainings/matrix', {
      params,
    });
    return response.data;
  }

  async getRevisionDetail(
    revisionId: number,
    params: GetTrainingMatrixRevisionDetailParams = {}
  ): Promise<TrainingMatrixRevisionDetailResponse> {
    const response = await api.get<TrainingMatrixRevisionDetailResponse>(
      `/trainings/matrix/${revisionId}`,
      { params }
    );
    return response.data;
  }

  async getSectionDocuments(
    params: GetTrainingMatrixSectionDocumentsParams
  ): Promise<TrainingMatrixSectionDocumentsResponse> {
    const response = await api.get<TrainingMatrixSectionDocumentsResponse>(
      '/trainings/matrix/documents',
      { params }
    );
    return response.data;
  }

  async updatePositionEntries(
    positionId: number,
    documentRevisionIds: number[]
  ): Promise<UpdateTrainingMatrixEntriesResponse> {
    const response = await api.put<UpdateTrainingMatrixEntriesResponse>(
      `/trainings/matrix/current/positions/${positionId}`,
      { documentRevisionIds }
    );
    return response.data;
  }

  async updateBatchEntries(
    positionIds: number[],
    documentRevisionIds: number[]
  ): Promise<UpdateTrainingMatrixEntriesResponse> {
    const response = await api.put<UpdateTrainingMatrixEntriesResponse>(
      '/trainings/matrix/current/documents',
      { positionIds, documentRevisionIds }
    );
    return response.data;
  }

  async deletePositionEntries(positionId: number): Promise<void> {
    await api.delete(`/trainings/matrix/current/positions/${positionId}`);
  }

  async deletePositionsEntries(
    positionIds: number[]
  ): Promise<DeleteTrainingMatrixPositionsEntriesResponse> {
    const response = await api.delete<DeleteTrainingMatrixPositionsEntriesResponse>(
      '/trainings/matrix/current/positions',
      { data: { positionIds } }
    );
    return response.data;
  }
}

export const trainingMatrixApi = new TrainingMatrixApi();
